import { useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { toast } from "sonner";
import { Inbox, BarChart3, PenSquare, Users, Bell, ExternalLink, Loader2, ShieldAlert, Images } from "lucide-react";
import { useAdminApi, playChime } from "./useAdminApi";
import LeadsPanel from "./LeadsPanel";
import AnalyticsPanel from "./AnalyticsPanel";
import BlogsPanel from "./BlogsPanel";
import UsersPanel from "./UsersPanel";
import GalleryPanel from "./GalleryPanel";
import { useNoIndex } from "@/pages/NotFound";

const tabs = [
  { to: "/admin", label: "Leads", icon: Inbox, end: true },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/blog", label: "Blog & AI Writer", icon: PenSquare },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/users", label: "Users", icon: Users },
];

const useLeadAlerts = (enabled) => {
  const api = useAdminApi();
  const [summary, setSummary] = useState({ unread: 0, total: 0 });
  const lastTotal = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const poll = async () => {
      try {
        const s = await api.get("/admin/leads/summary");
        if (!alive) return;
        if (lastTotal.current !== null && s.total > lastTotal.current && s.latest) {
          playChime();
          toast.success(`New lead: ${s.latest.name}`, { description: s.latest.email });
          if (window.Notification?.permission === "granted") new Notification("New lead – Dr. Cora", { body: `${s.latest.name} · ${s.latest.email}`, icon: "/icon-192.png" });
        }
        lastTotal.current = s.total;
        setSummary(s);
      } catch {}
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled, api]);
  return [summary, setSummary];
};

const Sidebar = ({ unread }) => {
  const [perm, setPerm] = useState(window.Notification?.permission);
  const enable = async () => setPerm(await Notification.requestPermission());
  return (
    <aside className="lg:w-64 lg:min-h-screen bg-[#080B0D] border-b lg:border-b-0 lg:border-r border-[#00C9B7]/10 p-4 lg:p-6 flex lg:flex-col gap-4">
      <div className="hidden lg:flex items-center gap-3 mb-6"><img src="/icon-192.png" alt="logo" className="w-10 h-10 rounded-full" /><div><p className="font-display text-lg text-[#1AA5A0]">Dr. Cora</p><p className="text-[10px] uppercase tracking-widest text-slate-500">Owner dashboard</p></div></div>
      <nav className="flex lg:flex-col gap-1 overflow-x-auto flex-1">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm whitespace-nowrap transition-colors ${isActive ? "bg-[#00C9B7]/15 text-[#00C9B7]" : "text-slate-400 hover:text-white hover:bg-white/5"}`} data-testid={`admin-tab-${t.label.split(" ")[0].toLowerCase()}`}>
            <t.icon size={18} /> {t.label}
            {t.label === "Leads" && unread > 0 && <span className="ml-auto bg-[#00C9B7] text-[#031312] text-xs font-bold rounded-full px-2 py-0.5" data-testid="admin-unread-badge">{unread}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="hidden lg:block space-y-3">
        {perm !== "granted" && window.Notification && <button onClick={enable} className="w-full flex items-center gap-2 text-xs text-slate-300 border border-[#00C9B7]/20 rounded-xl px-3 py-2.5 hover:border-[#00C9B7]" data-testid="admin-enable-alerts-btn"><Bell size={14} /> Enable desktop alerts</button>}
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-slate-400 hover:text-white px-3"><ExternalLink size={14} /> View website</a>
      </div>
      <div className="flex items-center"><UserButton /></div>
    </aside>
  );
};

const NoAccess = () => (
  <div className="min-h-screen flex flex-col items-center justify-center text-center p-6 gap-4" data-testid="admin-no-access">
    <ShieldAlert size={44} className="text-[#00C9B7]" />
    <p className="font-display text-3xl uppercase">Owner access only</p>
    <p className="text-slate-400 max-w-md">This account is not an owner account. Sign in with the clinic's owner email to view the dashboard.</p>
    <UserButton />
  </div>
);

const Dashboard = () => {
  const api = useAdminApi();
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/me").then(setMe).catch(() => setMe({ role: "none" })); }, [api]);
  const isAdmin = me?.role === "admin";
  const [summary, setSummary] = useLeadAlerts(isAdmin);
  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#00C9B7]" /></div>;
  if (!isAdmin) return <NoAccess />;
  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="admin-dashboard">
      <Sidebar unread={summary.unread} />
      <main className="flex-1 p-5 sm:p-8 lg:p-10 min-w-0">
        <Routes>
          <Route index element={<LeadsPanel onRead={() => setSummary((s) => ({ ...s, unread: 0 }))} />} />
          <Route path="analytics" element={<AnalyticsPanel />} />
          <Route path="blog" element={<BlogsPanel />} />
          <Route path="gallery" element={<GalleryPanel />} />
          <Route path="users" element={<UsersPanel />} />
        </Routes>
      </main>
    </div>
  );
};

export default function AdminApp() {
  useNoIndex();
  return (
    <div className="bg-[#0B0F12] text-slate-100 min-h-screen">
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </div>
  );
}
